import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import Container from '../components/common/Container';
import { useTranslation } from 'react-i18next';
import InputSearchBox from '../components/common/InputSearchBox';
import DateSearchBox from '../components/common/DateSearchBox';
import SelectSearchBox from '../components/common/SelectSearchBox';
import { Status, LegalPersonVerificationResult } from '../types/legalPersonVerification';
import Button from '../components/common/Button';
import LegalPersonVerificationDetailModal from '../components/modal/LegalPersonVerificationDetailModal';
import MemoModal from '../components/modal/MemoModal';
import MemoButton from '../components/common/MemoButton';
import { GET_LEGAL_PERSON_VERIFICATION } from '../graphql/queries';
import { formatDateTime } from '../utils/datetime';
import { SAVE_VERIFICATION_MEMO } from '../graphql/mutations';
import Loading from '../components/common/Loading';

type SortField = 'corporateName' | 'receivedDate' | 'verificationDate' | 'verificationMethod' | 'legalPersonStatus' | 'personalStatus' | 'attorneyStatus' | 'amlStatus' | 'snailMailStatus' | 'memo';
type SortDirection = 'asc' | 'desc';

const LegalPersonVerification: React.FC = () => {
    // const legalPersonInfo = useSelector((state: RootState) => state.legalPerson);
    // const dispatch = useDispatch();

    const { t } = useTranslation('verification');

    const [ corporateNumber, setCorporateNumber] = useState<string>('');
    const [ startDate, setStartDate ] = useState<string>('');
    const [ endDate, setEndDate ] = useState<string>('');
    const [ status, setStatus ] = useState<Status | 'all'>('all');
    const [ verificationResult, setVerificationResult ] = useState<LegalPersonVerificationResult | 'all'>('all');
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isMemoModalOpen, setIsMemoModalOpen] = useState(false);
    const [fieldName, setFieldName] = useState<string>('');


    const [totalPage, setTotalPage] = useState<number>(0);
    const [selectedPage, setSelectedPage] = useState<number>(1);
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [sortField, setSortField] = useState<SortField>('receivedDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const { loading, error, data, refetch } = useQuery(GET_LEGAL_PERSON_VERIFICATION, {
        variables: {
            id: null,
            receivedStartDate: null,
            receivedEndDate: null,
            status: null,
            verificationResult: null,
        }
    });

    const [saveVerificationMemo] = useMutation(SAVE_VERIFICATION_MEMO, {
        onCompleted: () => {
            console.log('Legal person reference created successfully');
            window.location.reload();
        },
        onError: (error) => {
            console.error('Error creating legal person reference:', error);
            alert(t('error creating legal person reference'));
        }
    });

    const sortedData = useMemo(() => {
        if (!data || !data.allLegalPersonVerification) return [];
        
        const sorted = [...data.allLegalPersonVerification].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortField) {
                case 'corporateName':
                    aValue = a.corporateName || '';
                    bValue = b.corporateName || '';
                    break;
                case 'receivedDate':
                    aValue = new Date(a.legalPersonVerification_receivedDate || 0);
                    bValue = new Date(b.legalPersonVerification_receivedDate || 0);
                    break;
                case 'verificationDate':
                    aValue = new Date(a.legalPersonVerification_verificationDate || 0);
                    bValue = new Date(b.legalPersonVerification_verificationDate || 0);
                    break;
                case 'verificationMethod':
                    aValue = a.legalPersonVerification_verificationMethod || '';
                    bValue = b.legalPersonVerification_verificationMethod || '';
                    break;
                case 'legalPersonStatus':
                    aValue = a.legalPersonVerification_status === "verified" ? a.legalPersonVerification_result : a.legalPersonVerification_status;
                    bValue = b.legalPersonVerification_status === "verified" ? b.legalPersonVerification_result : b.legalPersonVerification_status;
                    break;
                case 'personalStatus':
                    aValue = a.personalVerification_status === "verified" ? a.personalVerification_result : a.personalVerification_status;
                    bValue = b.personalVerification_status === "verified" ? b.personalVerification_result : b.personalVerification_status;
                    break;
                case 'attorneyStatus':
                    aValue = a.letterOfAttorney_status === "verified" ? a.letterOfAttorney_result : a.letterOfAttorney_status;
                    bValue = b.letterOfAttorney_status === "verified" ? b.letterOfAttorney_result : b.letterOfAttorney_status;
                    break;
                case 'amlStatus':
                    aValue = a.amlRiskCheck_status === "completed" ? a.amlRiskCheck_result : a.amlRiskCheck_status;
                    bValue = b.amlRiskCheck_status === "completed" ? b.amlRiskCheck_result : b.amlRiskCheck_status;
                    break;
                case 'snailMailStatus':
                    aValue = a.snailMailVerification_deliveryStatus === "verified" ? a.snailMailVerification_result : a.snailMailVerification_deliveryStatus;
                    bValue = b.snailMailVerification_deliveryStatus === "verified" ? b.snailMailVerification_result : b.snailMailVerification_deliveryStatus;
                    break;
                case 'memo':
                    aValue = a.legalPersonVerification_verificationMemo || '';
                    bValue = b.legalPersonVerification_verificationMemo || '';
                    break;
                default:
                    return 0;
            }
            
            if (aValue instanceof Date && bValue instanceof Date) {
                return sortDirection === 'asc' ? aValue.getTime() - bValue.getTime() : bValue.getTime() - aValue.getTime();
            }
            
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();
            
            if (sortDirection === 'asc') {
                return aStr.localeCompare(bStr);
            } else {
                return bStr.localeCompare(aStr);
            }
        });
        
        return sorted;
    }, [data, sortField, sortDirection]);

    useEffect(() => {
        if(sortedData) {
            setTotalPage(Math.max(1, Math.ceil(sortedData.length / 10)));
        }
    }, [sortedData]);

    const isValidVerificationResult = (value: string): value is LegalPersonVerificationResult | 'all' => {
        return Object.values(LegalPersonVerificationResult).includes(value as LegalPersonVerificationResult) || value === 'all';
    }

    const verificationResultOptions = useMemo(() => [
        { value: 'all', label: t('all') },
        ...Object.values(LegalPersonVerificationResult).map(result => ({
            value: result,
            label: t(result)
        }))
    ], []);

    // const isValidVerificationType = (value: string): value is VerificationType | 'all' => {
    //     return Object.values(VerificationType).includes(value as VerificationType) || value === 'all';
    // }

    // const verificationTypeOptions = useMemo(() => [
    //     { value: 'all', label: t('all') },
    //     ...Object.values(VerificationType).map(method => ({
    //         value: method,
    //         label: t(method)
    //     }))
    // ], []);

    const isValidStatus = (value: string): value is Status | 'all' => {
        return Object.values(Status).includes(value as Status) || value === 'all';
    }

    const statusOptions = useMemo(() => [
        { value: 'all', label: t('all') },
        ...Object.values(Status).map(method => ({
            value: method,
            label: t(method)
        }))
    ], []);

    const handleCloseDetailModal = () => setIsDetailModalOpen(false);
    const handleCloseMemoModal = () => setIsMemoModalOpen(false);
    const handelSetStatus = useCallback((value: string) => {
        if (isValidStatus(value)) {
            setStatus(value);
            console.log('Status set to:', value);
        } else {
            console.error('Invalid Status:', value);
        }
    }, []);
        const handleUpdateMemo = (value: string) => {
        saveVerificationMemo({
            variables: {
                verificationMemo: value,
                verificationPublicId: data.allLegalPersonVerification[selectedIndex].legalPersonVerification_verificationPublicId,
            }
        })
        .then(
            () => setIsMemoModalOpen(false)
        ).catch(
            err => console.log(err)
        )
    }
    // const handelSetVerificationType = useCallback((value: string) => {
    //     if (isValidVerificationType(value)) {
    //         setVerificationType(value);
    //     } else {
    //         console.error('Invalid Verification Type:', value);
    //     }
    // }, []);
    const handleSetVerificationResult = useCallback((value: string) => {
        if (isValidVerificationResult(value)) {
            setVerificationResult(value);
            console.log('Verification Result set to:', value);
        } else {
            console.error('Invalid Verification Result:', value);
        }
    }, []);

    const handleSort = useCallback((field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setSelectedPage(1);
    }, [sortField, sortDirection]);

    const getSortIcon = useCallback((field: SortField) => {
        if (sortField !== field) return ' ↕️';
        return sortDirection === 'asc' ? ' 🔼' : ' 🔽';
    }, [sortField, sortDirection]);

    const onHandlePrevPage = () => setSelectedPage(prev => prev - 1 < 1 ? 1 : prev - 1);
    const onHandleNextPage = () => setSelectedPage(prev => prev + 1 > totalPage ? prev : prev + 1);
    console.log('Current page:', selectedPage);
    if (loading) return <Loading />;
    if (error) return <p className="text-center mt-4 text-red-500">Error: {error.message}</p>;

    return (
        <Container label={t('legal person verification history')}>
            <LegalPersonVerificationDetailModal
                isOpen={isDetailModalOpen}
                onClose={handleCloseDetailModal}
                verificationDetailData={sortedData[selectedIndex]}
                fieldName={fieldName}
            />
            <MemoModal
                isOpen={isMemoModalOpen}
                onClose={handleCloseMemoModal}
                onConfirm={handleUpdateMemo}
                memoData={sortedData[selectedIndex].legalPersonVerification_verificationMemo}
                companyName={sortedData[selectedIndex].corporateName}
            />
            <div className="search-form">
                <InputSearchBox
                    inputValue={corporateNumber}
                    placeholder={t('search by company name or company number')}
                    onChange={setCorporateNumber}
                />
                <DateSearchBox
                    label={t('start date')}
                    inputValue={startDate}
                    onChange={setStartDate}
                />
                <DateSearchBox
                    label={t('end date')}
                    inputValue={endDate}
                    onChange={setEndDate}
                />
                <SelectSearchBox
                    label={t('status')}
                    options={statusOptions}
                    selectedValue={status}
                    onChange={handelSetStatus}
                />
                <SelectSearchBox
                    label={t('legal person verification result')}
                    options={verificationResultOptions}
                    selectedValue={verificationResult}
                    onChange={handleSetVerificationResult}
                />
                <div className="ml-20 inline-block">
                    <Button
                        label={t('search')}
                        onClick={() => {
                            console.log('Search with:', {
                                corporateNumber,
                                startDate,
                                endDate,
                                status,
                                verificationResult
                            });
                            refetch({
                                id: corporateNumber || null,
                                receivedStartDate: startDate || null,
                                receivedEndDate: endDate,
                                status: status === "all" ? null : status,
                                verificationResult: verificationResult === "all" ? null : verificationResult,
                            });
                            setSelectedPage(1);
                        }}
                    />
                </div>
                <div className="pl-20 inline-block cursor-pointer" 
                    onClick={() => {
                        console.log('Resetting search criteria');
                        setCorporateNumber('');
                        setStartDate('');
                        setEndDate('');
                        setStatus('all');
                        setVerificationResult('all');
                        refetch({
                            id: null,
                            receivedStartDate: null,
                            receivedEndDate: null,
                            status: null,
                            verificationResult: null,
                        });
                        setSelectedPage(1);
                        window.location.reload();
                    }}>
                    <span className="underline">{t('reset')}</span>
                </div>
            </div>
            <table>
                <thead>
                <tr>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('corporateName')}
                    >
                        {t('corporate name')}{getSortIcon('corporateName')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('receivedDate')}
                    >
                        {t('received datetime')}{getSortIcon('receivedDate')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('verificationDate')}
                    >
                        {t('verified datetime')}{getSortIcon('verificationDate')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('verificationMethod')}
                    >
                        {t('legal person verification method')}{getSortIcon('verificationMethod')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('legalPersonStatus')}
                    >
                        {t('legal person verification status')}{getSortIcon('legalPersonStatus')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('personalStatus')}
                    >
                        {t('personal verification status')}{getSortIcon('personalStatus')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('attorneyStatus')}
                    >
                        {t('letter of attorney status')}{getSortIcon('attorneyStatus')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('amlStatus')}
                    >
                        {t('aml risk check status')}{getSortIcon('amlStatus')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('snailMailStatus')}
                    >
                        {t('snail mail verification status')}{getSortIcon('snailMailStatus')}
                    </th>
                    <th 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('memo')}
                    >
                        メモ{getSortIcon('memo')}
                    </th>
                </tr>
                </thead>
                {sortedData.filter((_: any, index: number) => (index >= (selectedPage - 1) * 10) && (index < selectedPage * 10)).map((value: any, index: number) => {
                    return (
                        <tr className="group">
                            <td>{value.corporateName}</td>
                            <td>{formatDateTime(value.legalPersonVerification_receivedDate)}</td>
                            <td>{formatDateTime(value.legalPersonVerification_verificationDate)}</td>
                            <td>{value.legalPersonVerification_verificationMethod}</td>
                            <td
                                className={value.legalPersonVerification_result === "approved" ? 'status-completed' : (value.legalPersonVerification_result === "denied" ? 'status-failed' : 'status-in-progress')}>
                                <a href="#" className="no-underline hover:underline"
                                   key={index}
                                   onClick={() => {
                                       setSelectedIndex((selectedPage - 1) * 10 + index);
                                       setIsDetailModalOpen(true);
                                       setFieldName("legalPersonVerification");
                                   }}
                                >
                                    {value.legalPersonVerification_status === "verified" ? t(value.legalPersonVerification_result) : t(value.legalPersonVerification_status)}
                                </a>
                            </td>
                            <td
                                className={value.personalVerification_result === "approved" ? 'status-completed' : (value.personalVerification_result === "denied" ? 'status-failed' : 'status-in-progress')}>
                                <a href="#" className="no-underline hover:underline"
                                   key={index}
                                   onClick={() => {
                                       setSelectedIndex((selectedPage - 1) * 10 + index);
                                       setIsDetailModalOpen(true);
                                       setFieldName("personalVerification");
                                   }}
                                >
                                    {value.personalVerification_status === "verified" ? t(value.personalVerification_result) : t(value.personalVerification_status)}
                                </a>
                            </td>
                            <td
                                className={value.letterOfAttorney_result === "approved" ? 'status-completed' : (value.letterOfAttorney_result === "denied" ? 'status-failed' : 'status-in-progress')}>
                                <a href="#" className="no-underline hover:underline"
                                   key={index}
                                   onClick={() => {
                                       setSelectedIndex((selectedPage - 1) * 10 + index);
                                       setIsDetailModalOpen(true);
                                       setFieldName("letterOfAttorney");
                                   }}
                                >
                                    {value.letterOfAttorney_status === "verified" ? t(value.letterOfAttorney_result) : t(value.letterOfAttorney_status)}
                                </a>
                            </td>
                            <td
                                className={value.amlRiskCheck_result === "approved" ? 'status-completed' : (value.amlRiskCheck_result === "need_confirmation" ? 'status-failed' : 'status-in-progress')}>
                                <a href="#" className="no-underline hover:underline"
                                   key={index}
                                   onClick={() => {
                                       setSelectedIndex((selectedPage - 1) * 10 + index);
                                       setIsDetailModalOpen(true);
                                       setFieldName("amlRiskCheck");
                                   }}
                                >
                                    {value.amlRiskCheck_status === "completed" ? t(value.amlRiskCheck_result) : t(value.amlRiskCheck_status)}
                                </a>
                            </td>
                            <td
                                className={value.snailMailVerification_result === "approved" ? 'status-completed' : (value.snailMailVerification_result === "denied" ? 'status-failed' : 'status-in-progress')}>
                                <a href="#" className="no-underline hover:underline"
                                   key={index}
                                   onClick={() => {
                                       setSelectedIndex((selectedPage - 1) * 10 + index);
                                       setIsDetailModalOpen(true);
                                       setFieldName("snailMailVerification");
                                   }}
                                >
                                    {value.snailMailVerification_deliveryStatus === "verified" ? t(value.snailMailVerification_result) : t(value.snailMailVerification_deliveryStatus)}
                                </a>
                            </td>
                            <td>
                                <MemoButton
                                    hasMemo={!!value.legalPersonVerification_verificationMemo}
                                    onClick={() => {
                                        setSelectedIndex((selectedPage - 1) * 10 + index);
                                        setIsMemoModalOpen(true);
                                    }}
                                />
                            </td>
                        </tr>
                    )
                })}
            </table>
            <div className="pagination">
                <Button
                    label={t('prev page')}
                    onClick={onHandlePrevPage}
                    addedClass='mx-[10px]'
                />
                <span>{selectedPage}</span>
                &nbsp;/&nbsp;
                <span>{totalPage}</span>
                <Button
                    label={t('next page')}
                    onClick={onHandleNextPage}
                    addedClass='mx-[10px]'
                />
            </div>
        </Container>
    );
};

export default LegalPersonVerification;
